/**
 * Frontend observability shim. Centralises every "send this error to
 * a third-party tracker" call site so the owner can flip the entire
 * app onto Sentry / PostHog / Honeycomb / their own beacon endpoint
 * by populating ONE env var.
 *
 * Today the function is a no-op when `NEXT_PUBLIC_OBSERVABILITY_DSN`
 * (or `NEXT_PUBLIC_SENTRY_DSN`) is unset. The production behaviour
 * does not change unless the env var is set in CF Pages /
 * environment.
 *
 * When the env var IS present we POST a minimal envelope to it via
 * `navigator.sendBeacon` (falls back to fetch keepalive). The shape
 * mirrors Sentry's events endpoint enough that it will validate as
 * a custom HTTP receiver:
 *   {
 *     ts: <ISO>,
 *     message: <string>,
 *     stack?: <string>,
 *     context?: Record<string, unknown>,
 *     url: <location.href>,
 *     userAgent: <navigator.userAgent>,
 *     digest?: <Next.js error digest>,
 *   }
 *
 * Future: swap the body of `capture` for `Sentry.captureException` once
 * the SDK is installed and `init()` runs at app boot. The call sites
 * (error.tsx, global-error.tsx, api interceptor) do not change.
 */

type Severity = 'error' | 'warning' | 'info'

interface CaptureContext {
  digest?: string
  url?: string
  status?: number
  endpoint?: string
  user_id?: number | string
  [key: string]: unknown
}

function getDsn(): string | null {
  // Either of these wins; SENTRY_DSN documented historically, the
  // generic OBSERVABILITY_DSN lets you point at any beacon endpoint.
  if (typeof process === 'undefined') return null
  const dsn =
    process.env.NEXT_PUBLIC_OBSERVABILITY_DSN ||
    process.env.NEXT_PUBLIC_SENTRY_DSN ||
    null
  return dsn && dsn.trim().length > 0 ? dsn.trim() : null
}

function safeStack(err: unknown): string | undefined {
  if (err instanceof Error) return err.stack
  if (err && typeof err === 'object' && 'stack' in err) {
    const s = (err as { stack?: unknown }).stack
    return typeof s === 'string' ? s : undefined
  }
  return undefined
}

function describe(err: unknown): string {
  if (typeof err === 'string') return err
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown }
    if (typeof e.message === 'string') return e.message
  }
  try {
    return JSON.stringify(err)
  } catch {
    return 'Unserialisable error'
  }
}

/**
 * Capture an exception with optional structured context. Safe to call
 * from anywhere, server-render path included; will silently skip when
 * `window` / `navigator` are unavailable.
 *
 * NB-F9 (v62 2026-05-30): when `@sentry/nextjs` is initialized (DSN env
 * var set + consent allows), the SDK takes priority. Reports flow to
 * Sentry with full breadcrumb + release + replay context. The beacon
 * fallback path below stays as the backup when no Sentry DSN is set
 * but `NEXT_PUBLIC_OBSERVABILITY_DSN` IS, useful for self-hosted
 * Honeycomb / custom collectors.
 */
export function captureException(
  err: unknown,
  context: CaptureContext = {},
  severity: Severity = 'error',
): void {
  if (typeof window === 'undefined') return

  // Prefer Sentry SDK when present. Lazy require so this module
  // doesn't pull @sentry/nextjs into the server bundle.
  try {
    // Dynamic import via require avoids hoisting the dependency into
    // server-side bundles where it would fail. The lib's captureException
    // is a no-op when Sentry isn't initialized. (No eslint-disable here:
    // @typescript-eslint rules aren't loaded by this repo's ESLint config,
    // and a directive naming an unloaded rule is itself a lint error.)
    const sentryLib = require('./sentry') as typeof import('./sentry')
    sentryLib.captureException(err, context)
  } catch {
    // Sentry lib not loadable in this environment. Fall through.
  }

  const dsn = getDsn()
  if (!dsn) return

  const body = JSON.stringify({
    ts: new Date().toISOString(),
    severity,
    message: describe(err),
    stack: safeStack(err),
    context,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  })

  try {
    // sendBeacon survives page unload (useful when the error is the
    // reason for navigating away); falls back to fetch keepalive.
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' })
      const sent = navigator.sendBeacon(dsn, blob)
      if (sent) return
    }
    void fetch(dsn, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // Mode no-cors so cross-origin beacons (Sentry / Honeycomb)
      // succeed even when the response is opaque; we don't read it.
      mode: 'no-cors',
    }).catch(() => {
      // Last-resort beacon failure is silent; observability MUST NOT
      // surface its own errors back to the user.
    })
  } catch {
    // No-op
  }
}

/**
 * Capture a non-error structured event (e.g. "stripe webhook missing").
 * Same contract as captureException but with a string message.
 */
export function captureMessage(
  message: string,
  context: CaptureContext = {},
  severity: Severity = 'info',
): void {
  captureException(message, context, severity)
}
